import React, { useEffect, useRef, useState } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { post } from '../../api/apiClient';
import StatusBadge from '../monitoring/StatusBadge';

// ── Wire contract ────────────────────────────────────────────────────────────
// POST /tenants/:tenantId/admin-role/dry-run   body: { groupName: string }
// Server-owned endpoint (parallel dispatch). Coded against the spec exactly —
// this is a flat body, NOT wrapped in the { success, data } envelope used elsewhere
// in tenantApi.ts.
export interface AdminRoleDryRunResult {
    tenantExists: boolean;
    databaseName: string | null;
    groupName: string;
    matchExpression: Record<string, unknown> | null;
    matched: number;
    total: number;
    samples: string[];
    valid: boolean;
    validationError: string | null;
}

// A SysAdmin condition matching more than this share of an EXISTING tenant's users is
// treated as the catastrophic-exposure failure mode, not a working condition — flagged
// loudly (red), never as success. Not returned by the server; a client-side ceiling.
const SANITY_CEILING_RATIO = 0.5;
const MAX_SAMPLES_SHOWN = 10;
const DEBOUNCE_MS = 500;

type PreviewStatus = 'ok' | 'fail' | 'warn' | 'unknown';

function classifyDryRun(r: AdminRoleDryRunResult): { status: PreviewStatus; label: string; message: string } {
    if (!r.valid) {
        return {
            status: 'fail',
            label: 'INVALID',
            message: r.validationError || 'This condition is invalid and cannot be saved.',
        };
    }
    if (!r.tenantExists) {
        return {
            status: 'unknown',
            label: 'NEW TENANT',
            message: 'This tenant does not exist yet, so there are 0 of 0 users to match against. '
                + 'That is expected — the first person who signs in through this Azure AD group will be granted SysAdmin automatically.',
        };
    }
    if (r.total === 0) {
        return {
            status: 'unknown',
            label: '0 USERS',
            message: 'This tenant has 0 users yet, so there are 0 of 0 to match against. '
                + 'That is expected — the first person who signs in through this Azure AD group will be granted SysAdmin automatically.',
        };
    }
    const ratio = r.matched / r.total;
    if (ratio > SANITY_CEILING_RATIO) {
        return {
            status: 'fail',
            label: 'TOO BROAD',
            message: `Matches ${r.matched} of ${r.total} users (${Math.round(ratio * 100)}%). `
                + 'That is an implausibly large share for a platform-admin condition — it would grant SysAdmin to most '
                + 'of the tenant, not a small admin group. Narrow the group name before proceeding.',
        };
    }
    if (r.matched === 0) {
        return {
            status: 'warn',
            label: '0 MATCHES',
            message: `Matches 0 of ${r.total} users. If anyone has already signed in through this Azure AD, `
                + 'double-check the group name — otherwise this is expected until the first login.',
        };
    }
    return {
        status: 'ok',
        label: 'MATCHES',
        message: `Matches ${r.matched} of ${r.total} users (${Math.round(ratio * 100)}%).`,
    };
}

const boxClass: Record<PreviewStatus | 'blank' | 'unreachable' | 'loading', string> = {
    ok:          'border-green-700 bg-green-900/20 text-green-300',
    fail:        'border-red-700 bg-red-900/20 text-red-300',
    warn:        'border-yellow-700 bg-yellow-900/20 text-yellow-300',
    unknown:     'border-gray-600 bg-gray-800/60 text-gray-300',
    blank:       'border-red-700 bg-red-900/20 text-red-300',
    unreachable: 'border-gray-600 bg-gray-800/60 text-gray-400',
    loading:     'border-gray-700 bg-gray-800/40 text-gray-400',
};

interface AdminRoleConditionEditorProps {
    /** Real or candidate tenant id. When blank (new-tenant wizard flow) a stable internal
     *  placeholder is used so the dry-run still round-trips through server-side validation
     *  and honestly comes back `tenantExists: false`. */
    tenantId?: string;
    groupName: string;
    onGroupNameChange: (value: string) => void;
    idpAlias: string;
    onIdpAliasChange: (value: string) => void;
    /** null = not yet known (unreachable / not yet checked) — must never block Next.
     *  false = blank value or server said `valid: false` — must block Next. */
    onValidityChange?: (valid: boolean | null) => void;
}

const inputClass = 'w-full px-3 py-2 rounded bg-gray-700 border border-gray-600 text-gray-100 placeholder-gray-500 focus:outline-none focus:border-blue-500 text-sm';

const AdminRoleConditionEditor: React.FC<AdminRoleConditionEditorProps> = ({
    tenantId, groupName, onGroupNameChange, idpAlias, onIdpAliasChange, onValidityChange,
}) => {
    // Stable placeholder used only when no real tenant id is known yet — never regenerated
    // mid-session, so re-runs of the same draft config are comparable.
    const placeholderIdRef = useRef(crypto.randomUUID());
    const effectiveTenantId = tenantId?.trim() || placeholderIdRef.current;

    const [result, setResult] = useState<AdminRoleDryRunResult | null>(null);
    const [state, setState] = useState<'idle' | 'loading' | 'ok' | 'unreachable' | 'blank'>('idle');
    const [errorDetail, setErrorDetail] = useState<string | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const requestTokenRef = useRef(0);

    const runDryRun = () => {
        const trimmed = groupName.trim();
        if (!trimmed) {
            setState('blank');
            setResult(null);
            onValidityChange?.(false);
            return;
        }
        const token = ++requestTokenRef.current;
        setState('loading');
        post<AdminRoleDryRunResult>(`/tenants/${encodeURIComponent(effectiveTenantId)}/admin-role/dry-run`, { groupName: trimmed })
            .then(r => {
                if (requestTokenRef.current !== token) return; // stale response
                setResult(r);
                setState('ok');
                setErrorDetail(null);
                onValidityChange?.(r.valid);
            })
            .catch((err: any) => {
                if (requestTokenRef.current !== token) return;
                setResult(null);
                setState('unreachable');
                setErrorDetail(err?.message ?? 'The dry-run check could not be reached.');
                // Unreachable is ambiguous, never a hard failure — do not block Next on it.
                onValidityChange?.(null);
            });
    };

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(runDryRun, DEBOUNCE_MS);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupName, effectiveTenantId]);

    const classified = result ? classifyDryRun(result) : null;

    return (
        <div className="border border-gray-700 rounded-lg p-4 bg-gray-900/40">
            <h3 className="text-sm font-semibold text-gray-300 mb-1">Platform Admin Role (SysAdmin)</h3>
            <p className="text-xs text-gray-500 mb-3">
                Anyone who signs in through this Azure AD and lands in this Keycloak group is automatically granted
                platform SysAdmin — no seeded admin user needed. Defaults to the Ontoris AD; point this at a
                customer's own AD only for a self-hosted deployment.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-1">
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                        Admin Group Name<span className="text-red-400 ml-1">*</span>
                    </label>
                    <input
                        className={inputClass}
                        value={groupName}
                        onChange={e => onGroupNameChange(e.target.value)}
                        placeholder="ontoris-platform-admins"
                    />
                    <p className="mt-1 text-xs text-gray-500">Keycloak realm group whose members get SysAdmin.</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">IdP Alias</label>
                    <input
                        className={inputClass}
                        value={idpAlias}
                        onChange={e => onIdpAliasChange(e.target.value)}
                        placeholder="microsoft"
                    />
                    <p className="mt-1 text-xs text-gray-500">Which configured IdP assigns the group on login.</p>
                </div>
            </div>

            {/* ── Dry-run preview ────────────────────────────────────────────────── */}
            <div className={`mt-3 rounded border px-3 py-2.5 text-sm ${boxClass[state === 'blank' ? 'blank' : state === 'unreachable' ? 'unreachable' : state === 'loading' ? 'loading' : (classified?.status ?? 'unknown')]}`}>
                {state === 'blank' && (
                    <div className="flex items-start gap-2">
                        <StatusBadge status="fail" label="REQUIRED" />
                        <span>A blank group name would produce an empty condition — the role would grant SysAdmin to nobody. Enter a group name to continue.</span>
                    </div>
                )}

                {state === 'loading' && (
                    <div className="flex items-center gap-2 text-gray-400">
                        <FiRefreshCw className="h-3 w-3 animate-spin" />
                        Checking how many users this would match…
                    </div>
                )}

                {state === 'unreachable' && (
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <StatusBadge status="unknown" label="UNKNOWN" />
                            <span className="font-medium">Could not verify this condition.</span>
                        </div>
                        <p className="text-xs opacity-80">
                            The dry-run check did not respond ({errorDetail}). This is unknown, not a pass — retry
                            before relying on this condition.
                        </p>
                        <button
                            type="button"
                            onClick={runDryRun}
                            className="mt-2 inline-flex items-center gap-1.5 rounded border border-gray-500 px-2 py-1 text-xs text-gray-300 hover:bg-gray-700"
                        >
                            <FiRefreshCw className="h-3 w-3" /> Retry
                        </button>
                    </div>
                )}

                {state === 'ok' && classified && result && (
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <StatusBadge status={classified.status} label={classified.label} />
                            <span className="font-medium">{classified.message}</span>
                        </div>
                        {result.samples.length > 0 && (
                            <div className="mt-1.5 text-xs opacity-90">
                                <span className="opacity-70">Sample matches: </span>
                                {result.samples.slice(0, MAX_SAMPLES_SHOWN).join(', ')}
                                {result.samples.length > MAX_SAMPLES_SHOWN && ` (+${result.samples.length - MAX_SAMPLES_SHOWN} more)`}
                            </div>
                        )}
                        {result.matchExpression && (
                            <details className="mt-2">
                                <summary className="text-xs opacity-70 cursor-pointer">Compiled condition</summary>
                                <pre className="mt-1 max-h-32 overflow-auto rounded bg-black/30 px-2 py-1 font-mono text-[11px] text-gray-300 whitespace-pre-wrap break-all">
                                    {JSON.stringify(result.matchExpression, null, 2)}
                                </pre>
                            </details>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminRoleConditionEditor;
